import { useEffect, useState } from "react";
import { getLocations } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { FALLBACK_IMG } from "@/lib/images";
import { Clock, MapPin, Navigation, Phone, Store } from "lucide-react";

export default function Locations({ type = "store" }) {
  const [data, setData] = useState({ locations: [], cities: [] });
  const [city, setCity] = useState(null);
  const isStore = type === "store";

  useEffect(() => {
    getLocations({ type, ...(city ? { city } : {}) }).then((res) => {
      setData(res);
    }).catch(() => {});
  }, [type, city]);

  const title = isStore ? "Mağazalar" : "Kiosklar";
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: data.locations.map((l, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "LocalBusiness",
        name: l.name,
        address: l.address,
        telephone: l.phone,
        openingHours: l.hours,
        url: `https://www.shuletaki.com.tr/${isStore ? "magazalar" : "kiosklar"}#${l.slug}`,
        image: l.image,
      },
    })),
  };

  return (
    <>
      <Seo title={title} description={`Shule ${title.toLowerCase()} — sana en yakın noktayı bul.`} jsonLd={jsonLd} path={isStore ? "/magazalar" : "/kiosklar"} />
      <section className="bg-shule-cream/60 border-b border-shule-line">
        <div className="container-shule py-14 lg:py-20">
          <Reveal>
            <p className="eyebrow">{isStore ? "Butik Deneyimi" : "AVM Noktaları"}</p>
            <h1 data-testid="locations-title" className="mt-3 font-serif text-5xl lg:text-6xl font-light text-shule-coffee">
              {isStore ? <>Kemerli nişlerin <span className="italic text-shule-goldhover">ardına</span> bekleriz</> : <>Alışverişine <span className="italic text-shule-goldhover">ara verme,</span> yanındayız</>}
            </h1>
            <p className="mt-4 text-shule-mocha max-w-lg leading-relaxed">
              {isStore
                ? "Mağazalarımızda her parçayı deneyebilir, stil danışmanlarımızdan öneri alabilirsin."
                : "Kiosklarımızda hızlı hediye seçkisi ve sezonun favorileri seni bekliyor."}
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-shule py-12 lg:py-16">
        {isStore && (
          <Reveal className="mb-10 grid md:grid-cols-2 gap-5">
            <article className="rounded-[2rem] border border-shule-line bg-shule-paper p-7">
              <p className="eyebrow">DENİZLİ</p><h2 className="mt-2 font-serif text-3xl text-shule-coffee">Shule · TerasPark</h2>
              <p className="mt-3 text-sm text-shule-mocha leading-relaxed">TerasPark AVM'deki Shule mağazamız. Mağaza deneyimi, güncel içerikler ve yol tarifi bu merkezde bir araya geliyor.</p>
              <div className="mt-5 flex flex-wrap gap-2"><a className="btn-outline-shule" href="https://www.google.com/maps/search/?api=1&query=Shule+TerasPark+Denizli" target="_blank" rel="noopener noreferrer">Yol Tarifi</a><a className="btn-outline-shule" href="https://www.teraspark.com.tr/" target="_blank" rel="noopener noreferrer">AVM'yi İncele</a></div>
            </article>
            <article className="rounded-[2rem] border border-shule-gold/30 bg-shule-goldlight/20 p-7">
              <p className="eyebrow">BURSA · YAKINDA</p><h2 className="mt-2 font-serif text-3xl text-shule-coffee">Shule · Anatolium</h2>
              <p className="mt-3 text-sm text-shule-mocha leading-relaxed">Alaşar Mah. İstanbul Cad. No:487, Osmangazi / Bursa. Yeni Shule mağazamız açılışa hazırlanıyor.</p>
              <div className="mt-5 flex flex-wrap gap-2"><a className="btn-outline-shule" href="https://www.google.com/maps/search/?api=1&query=Anatolium+Bursa" target="_blank" rel="noopener noreferrer">AVM'ye Yol Tarifi</a><a className="btn-outline-shule" href="https://www.anatolium-bursa.com/tr" target="_blank" rel="noopener noreferrer">AVM'yi İncele</a></div>
            </article>
          </Reveal>
        )}
        {isStore && <Reveal className="mb-12 rounded-[2rem] border border-shule-line bg-shule-paper p-7 lg:p-9"><p className="eyebrow">MAĞAZALARDAN</p><h2 className="mt-2 font-serif text-3xl text-shule-coffee">Shule mağaza günlüğü</h2><p className="mt-3 max-w-2xl text-sm text-shule-mocha leading-relaxed">Yeni koleksiyonlar, mağaza içi etkinlikler, açılış hazırlıkları ve gerçek mağaza fotoğrafları için yerel içerik alanı. Anatolium Bursa açılış süreci de burada güncellenecek.</p></Reveal>}
        {!isStore && (
          <Reveal className="mb-10 rounded-[2rem] border border-shule-gold/30 bg-shule-goldlight/20 overflow-hidden grid lg:grid-cols-2 items-stretch">
            <div className="p-6 sm:p-8 flex flex-col justify-center">
              <p className="eyebrow">Kiosk Formatı</p>
              <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-shule-coffee">3 × 3 m modüler Shule kiosku</h2>
              <p className="mt-2 text-sm text-shule-mocha leading-relaxed max-w-lg">
                Kemerli nişleri, dış cephede askılı ürün alanları, cam tezgâh ve kompakt kasa kurgusuyla; AVM trafiğinin kalbinde tam bir Shule deneyimi sunar. Zarafet detayda, güzellik seninle.
              </p>
              <a data-testid="kiosk-franchise-cta" href="/bayilik" className="btn-outline-shule whitespace-nowrap mt-5 self-start">Kiosk Bayiliği Al</a>
            </div>
            <div className="relative min-h-[260px]">
              <img src="/assets/img/kiosk-hero.jpeg" alt="Shule 3x3 metre kiosk konsepti" loading="lazy"
                className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </Reveal>
        )}
        <div data-testid="city-filters" className="flex gap-2 flex-wrap mb-10">
          <button data-testid="loc-city-all" onClick={() => setCity(null)}
            className={`min-h-[44px] px-5 rounded-full border text-sm font-medium transition-colors ${!city ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent"}`}>
            Tüm Şehirler
          </button>
          {data.cities.map((c) => (
            <button key={c} data-testid={`loc-city-${c}`} onClick={() => setCity(c)}
              className={`min-h-[44px] px-5 rounded-full border text-sm font-medium transition-colors ${city === c ? "bg-shule-coffee text-shule-paper border-shule-coffee" : "border-shule-lineaccent"}`}>
              {c}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {data.locations.map((l, i) => (
              <Reveal key={l.slug} delay={i * 0.06}>
                <article data-testid={`location-card-${l.slug}`} className="rounded-[1.75rem] border border-shule-line bg-shule-paper overflow-hidden hover:shadow-[0_20px_45px_-18px_rgba(61,46,40,0.2)] transition-shadow duration-500">
                  <div className="relative h-44 overflow-hidden">
                    <img src={l.image} alt={l.name} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }} className="w-full h-full object-cover" />
                    <span className="absolute top-3 left-3 badge-tag !bg-shule-paper/90 inline-flex items-center gap-1">
                      {l.type === "store" ? <Store className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                      {l.type === "store" ? "Mağaza" : "Kiosk"}
                    </span>
                  </div>
                  <div className="p-5">
                    <h2 className="font-serif text-2xl leading-tight">{l.name}</h2>
                    <p className="mt-2 text-sm text-shule-mocha flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-shule-gold" />{l.address}</p>
                    <p className="mt-1.5 text-sm text-shule-mocha flex items-center gap-2"><Clock className="w-4 h-4 shrink-0 text-shule-gold" />{l.hours}</p>
                    <p className="mt-1.5 text-sm text-shule-mocha flex items-center gap-2"><Phone className="w-4 h-4 shrink-0 text-shule-gold" />{l.phone}</p>
                    {l.features?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {l.features.map((f) => <span key={f} className="badge-tag">{f}</span>)}
                      </div>
                    )}
                    <a data-testid={`directions-${l.slug}`} href={`https://www.google.com/maps/search/${encodeURIComponent(l.name + " " + l.address)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-shule-goldhover hover:underline underline-offset-4 min-h-[44px]">
                      <Navigation className="w-4 h-4" /> Yol Tarifi Al
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal className="lg:sticky lg:top-28 self-start">
            <div data-testid="map-preview" className="relative rounded-[2rem] border border-shule-line overflow-hidden bg-gradient-to-br from-shule-cream via-shule-pudra/50 to-shule-oak/40 aspect-[4/5] grain">
              <div className="absolute inset-6 border border-shule-gold/25 rounded-[1.5rem]" aria-hidden="true" />
              {data.locations.map((l, i) => {
                const pos = [{ t: "22%", l: "30%" }, { t: "45%", l: "62%" }, { t: "62%", l: "28%" }, { t: "30%", l: "70%" }][i % 4];
                return (
                  <div key={l.slug} className="absolute group" style={{ top: pos.t, left: pos.l }}>
                    <span className="block w-4 h-4 rounded-full bg-shule-gold border-2 border-shule-paper shadow-md" />
                    <span className="absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap text-[11px] font-semibold bg-shule-paper border border-shule-line rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {l.name}
                    </span>
                  </div>
                );
              })}
              <p className="absolute bottom-6 left-0 right-0 text-center text-xs text-shule-soft">Temsili harita önizlemesi</p>
            </div>
          </Reveal>
        </div>
        
      </div>
    </>
  );
}
